import { useState } from "react";

export type SortField = "name" | "cas" | "created" | "updated";
export type SortDir = "asc" | "desc";
export interface SortState {
  field: SortField;
  dir: SortDir;
}

// 既定値を「変更日」に変更したため、旧キーの保存値を無効化する（_v2）
const STORAGE_KEY = "reagentSort_v2";
const DEFAULT: SortState = { field: "updated", dir: "desc" };

// 各基準の既定の並び順（登録日・変更日は最新順＝降順）
export function defaultDir(field: SortField): SortDir {
  return field === "created" || field === "updated" ? "desc" : "asc";
}

function isValid(s: any): s is SortState {
  return (
    s &&
    ["name", "cas", "created", "updated"].includes(s.field) &&
    (s.dir === "asc" || s.dir === "desc")
  );
}

export function useSort(): [SortState, (s: SortState) => void] {
  const [sort, setSort] = useState<SortState>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (isValid(parsed)) return parsed;
      }
    } catch {
      // 破損時は既定値
    }
    return DEFAULT;
  });

  function update(next: SortState) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setSort(next);
  }

  return [sort, update];
}

/* =======================================================================
   試薬（ボトル）画面用の並び替え。
   マスター画面とは別の localStorage キーで独立して管理する。
   ======================================================================= */
export type ReagentSortField = "name" | "quantity" | "created" | "updated";
export interface ReagentSortState {
  field: ReagentSortField;
  dir: SortDir;
}

// 既定値を「変更日」に変更したため、旧キーの保存値を無効化する（_v2）
const REAGENT_STORAGE_KEY = "reagentBottleSort_v2";
const REAGENT_DEFAULT: ReagentSortState = { field: "updated", dir: "desc" };

export function reagentDefaultDir(field: ReagentSortField): SortDir {
  return field === "created" || field === "updated" ? "desc" : "asc";
}

function isReagentValid(s: any): s is ReagentSortState {
  return (
    s &&
    ["name", "quantity", "created", "updated"].includes(s.field) &&
    (s.dir === "asc" || s.dir === "desc")
  );
}

export function useReagentSort(): [
  ReagentSortState,
  (s: ReagentSortState) => void
] {
  const [sort, setSort] = useState<ReagentSortState>(() => {
    try {
      const raw = localStorage.getItem(REAGENT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (isReagentValid(parsed)) return parsed;
      }
    } catch {
      // 破損時は既定値
    }
    return REAGENT_DEFAULT;
  });

  function update(next: ReagentSortState) {
    localStorage.setItem(REAGENT_STORAGE_KEY, JSON.stringify(next));
    setSort(next);
  }

  return [sort, update];
}
